<!--
  Section component for creating structured content areas with customizable styling.
  @component

  @prop containerWidth: 'wide' | 'normal' | 'slim' | 'tight' - The width of the container.
  @prop colorVariant: ColorVariant - The color variant of the section.
  @prop rootStyles: SystemStyleObject - Additional styles for the root element.
  @prop containerStyles: SystemStyleObject - Additional styles for the container element.
  @prop as: 'section' | 'header' | 'footer' | 'div' - The HTML element to render the section as.
-->


<script lang="ts">
  import { css } from '@agapi/ui/panda/css';
  import { container } from '@agapi/ui/panda/patterns';
  import { token, dynamicColorStyles } from '@agapi/ui/panda/utils';
  import type { Token } from '@agapi/ui/panda/tokens';

  import { containerBaseStyles, sectionStyles } from './section.styles';
  import { useActions } from '../../utils/useAction';
  import type { SectionProps } from './section.types';

  /**
   * The props for the Section component.
   * @type {SectionProps}
   */
  const {
    use,
    children,
    containerWidth = 'normal',
    colorVariant = 'default',
    rootStyles,
    containerStyles,
    as = 'section',
    ...props
  }: SectionProps = $props();

</script>

<svelte:element
  this={as}
  {...props}
  use:useActions={use || []}
  class={css(sectionStyles, rootStyles)}
  style="
    background-color: {dynamicColorStyles(colorVariant).backgroundColor};
    color: {dynamicColorStyles(colorVariant).color};
  ">

  <div
    class={css(container.raw(), containerBaseStyles, containerStyles)}
    style="max-width: {token(`sizes.container.${containerWidth}` as Token)};"
  >
    {#if children}
      {@render children()}
    {/if}
  </div>
</svelte:element>
